import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

// Components
import LoadMoreListings from 'src/Components/homeSearch/Listings/LoadMoreListings'
import { useFavoriteListingClickHandler } from 'src/hooks/listings'
import ListingCard from 'src/Components/homeSearch/ListingCard'

// Selectors
import {
  selectSearchListingsTotal,
  selectFavoriteListingIds,
  selectHomebotListingsSearchLoading,
  selectSelectedListingId,
} from 'src/store/selectors/listings'

// Actions
import { setSelectedListingId } from 'src/actions/listings'

// Helpers
import { fetchFavoriteListings } from 'src/actions/favoriteListings'
import { PATH_HELPERS } from 'src/constants/navigation'

// Styles
import styles from './ListView.module.scss'

const ListView = ({
  listings,
  customerId,
  onFocus,
  focusedListing,
  setFocusedListing,
  listingsDispatch,
  assignListingRef,
}) => {
  const dispatch = useDispatch()
  const favoriteListingIds = useSelector(selectFavoriteListingIds)
  const loading = useSelector(selectHomebotListingsSearchLoading)
  const listingsTotal = useSelector(selectSearchListingsTotal)
  const handleFavoriteClick = useFavoriteListingClickHandler()
  const selectedListingId = useSelector(selectSelectedListingId)

  useEffect(() => {
    dispatch(fetchFavoriteListings(customerId))
  }, [dispatch, customerId])

  useEffect(() => {
    const selectedListing = listings.find(
      listing => listing.id === selectedListingId
    )

    if (selectedListing) {
      // TODO-scroll to listing if in listview, fetch and append the listing if not
      // selectedListing?.ref?.current?.scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'start',
      // })
      setFocusedListing(selectedListing)
    }
  }, [])

  const handleTouchStart = listing => {
    setFocusedListing(listing)
  }

  return (
    <>
      <div>
        <div name='list-top' />
        <ul className={styles.listView}>
          {listings?.map((listing, index) => {
            return (
              <ListViewItem
                key={listing.id}
                listing={listing}
                index={index}
                listingsDispatch={listingsDispatch}
                customerId={customerId}
                onFocus={onFocus}
                handleTouchStart={handleTouchStart}
                handleFavoriteClick={handleFavoriteClick}
                favoriteListingIds={favoriteListingIds}
                focusedListing={focusedListing}
                listings={listings}
                assignListingRef={assignListingRef}
              />
            )
          })}
          <li name='list-bottom' />
        </ul>
      </div>
      {!loading && listings?.length < listingsTotal && (
        <div className={styles.loadMoreListingsWrapper}>
          <LoadMoreListings />
        </div>
      )}
    </>
  )
}

const ListViewItem = ({
  listing,
  listingsDispatch,
  customerId,
  onFocus,
  handleTouchStart,
  handleFavoriteClick,
  favoriteListingIds,
  focusedListing,
  assignListingRef,
}) => {
  const dispatch = useDispatch()
  const ref = useRef(null)

  useEffect(() => {
    listingsDispatch({
      type: assignListingRef,
      listing: listing,
      ref: ref,
    })
  }, [assignListingRef, listing, listingsDispatch])

  const handleOnClick = () => {
    dispatch(setSelectedListingId(listing.id))
  }

  return (
    <li tabIndex={-1}>
      <Link
        tabIndex={0}
        className={styles.link}
        to={PATH_HELPERS.listing.buildPath(customerId, listing.id)}
        onClick={handleOnClick}
      >
        <div
          onMouseEnter={() => onFocus(listing)}
          onMouseLeave={() => onFocus(null)}
          onFocus={() => onFocus(listing)}
          onTouchStart={() => handleTouchStart(listing)}
        >
          <ListingCard
            ref={ref}
            listing={listing}
            favoriteClick={handleFavoriteClick}
            focused={listing.id === focusedListing?.id}
            isFavorite={
              favoriteListingIds && favoriteListingIds.includes(listing.id)
            }
          />
        </div>
      </Link>
    </li>
  )
}

ListView.propTypes = {
  listings: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  onFocus: PropTypes.func,
  setFocusedListing: PropTypes.func,
  listingsDispatch: PropTypes.func,
  assignListingRef: PropTypes.string,
  focusedListing: PropTypes.shape({
    id: PropTypes.string,
  }),
}

ListViewItem.propTypes = {
  listings: PropTypes.array,
  listing: PropTypes.shape({
    id: PropTypes.string,
    address: PropTypes.object,
    bathroomsCount: PropTypes.number,
    bedroomsCount: PropTypes.number,
    priceCents: PropTypes.number,
    sqftTotal: PropTypes.number,
    rentPriceCents: PropTypes.number,
    coordinates: PropTypes.object,
    listingUrl: PropTypes.string,
    priceFormatted: PropTypes.string,
    rentPriceFormatted: PropTypes.string,
    status: PropTypes.string,
    vector: PropTypes.string,
    mlsNumber: PropTypes.string,
    thumbnailImageFileNames: PropTypes.array,
  }),
  customerId: PropTypes.string.isRequired,
  onFocus: PropTypes.func,
  setFocusedListing: PropTypes.func,
  listingsDispatch: PropTypes.func,
  handleFavoriteClick: PropTypes.func,
  handleTouchStart: PropTypes.func,
  assignListingRef: PropTypes.string,
  favoriteListingIds: PropTypes.array,
  focusedListing: PropTypes.shape({
    id: PropTypes.string,
  }),
}

export default ListView
